import React, { useState, useEffect, useRef } from 'react';
import ArticleNotFoundModal from '../common/ArticleNotFoundModal';
import MultiCheckboxSelect from '../common/MultiCheckboxSelect';
import pieceService from '../../services/pieceService';
import articleService from '../../services/articleService';
import { useTheme } from '../../contexts/ThemeContext';
import interventionService from '../../services/interventionService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArticleHistoryModal from '../common/ArticleHistoryModal';
import { openBarcodeScanner } from '../../utils/barcodeScanner';

const EntretienPage = ({ intervention }) => {
  const { isDarkMode } = useTheme();
  const [codeABarre, setCodeABarre] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const scannerRef = useRef(null);
  const [form, setForm] = useState(() => {
    if (intervention) {
      return {
        date: intervention.startDate || '',
        designation: '',
        capacite: '',
        marque: '',
        numeroSerie: '',
        observation: '',
        local: intervention.local?.adresse || '',
        responsable: intervention.local?.responsable || '',
        telResponsable: intervention.local?.telResponsable || '',
        client: intervention.client?.raisonSocial || '',
        famille: '',
        type: '',
        piece: '',
        qte: '',
      };
    }
    return {
      date: '',
      designation: '',
      capacite: '',
      marque: '',
      numeroSerie: '',
      observation: '',
      local: '',
      responsable: '',
      telResponsable: '',
      client: '',
      famille: '',
      type: '',
      piece: '',
      qte: '',
    };
  });
  const [errors, setErrors] = useState({});
  const [pieces, setPieces] = useState([]);
  const [selectedPieces, setSelectedPieces] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  // Barcode article info and status
  const [articleInfo, setArticleInfo] = useState(null);
  const [barcodeStatusColor, setBarcodeStatusColor] = useState('#22c55e');
  const [barcodeTimeout, setBarcodeTimeout] = useState(null);
  // State for filtered famille by barcode
  const [filteredFamille, setFilteredFamille] = useState(null);
  // Disable all selects by default, enable only if article found
  const [selectsDisabled, setSelectsDisabled] = useState(true);
  // Modal for article creation
  const [showCreateArticleModal, setShowCreateArticleModal] = useState(false);
  // Search filter state for famille/pieces/services
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingPieces, setLoadingPieces] = useState(false);
  const [errorPieces, setErrorPieces] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showEditArticleModal, setShowEditArticleModal] = useState(false);
  const [editArticleData, setEditArticleData] = useState(null);
  const [expanded, setExpanded] = React.useState(true);

  // Stop scanning function
  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
      scannerRef.current = null;
    }
    setBarcodeScanning(false);
  };

  // Start barcode scanning using Quagga2
  const startBarcodeScanning = () => {
    setBarcodeScanning(true);
    setShowCamera(true);
    const scanner = openBarcodeScanner({
      modalId: 'barcode-scanner-modal-entretien',
      scannerId: 'quagga-reader-entretien',
      onDetected: (barcode) => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        handleScanResult(barcode);
      },
      onError: (err) => {
        console.error('Scanner error:', err);
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Caméra non disponible. Saisissez le code-barres manuellement:');
        if (userInput && userInput.trim()) {
          handleScanResult(userInput.trim());
        }
      },
      onManualEntry: () => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Saisissez le code-barres manuellement (max 8 chiffres):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            handleScanResult(trimmedInput);
          }
        }
      },
    });
    scannerRef.current = scanner;
  };

  // Fetch all pieces/services on mount
  useEffect(() => {
    setLoadingPieces(true);
    pieceService.getAllPieces()
      .then(res => {
        if (res.success) setPieces(res.data);
        else setErrorPieces(res.message || 'Erreur lors du chargement');
      })
      .catch(() => setErrorPieces('Erreur lors du chargement'))
      .finally(() => setLoadingPieces(false));
  }, []);

  // Remove barcode debounce effect: clear state only when input is cleared
  useEffect(() => {
    if (!codeABarre) {
      setArticleInfo(null);
      setBarcodeStatusColor('#22c55e');
      setFilteredFamille(null);
      setSelectsDisabled(true);
      setShowCreateArticleModal(false);
    }
  }, [codeABarre]);

  // If intervention changes (e.g. on navigation), update form
  useEffect(() => {
    if (intervention) {
      setForm(f => ({
        ...f,
        date: intervention.startDate || '',
        local: intervention.local?.adresse || '',
        responsable: intervention.local?.responsable || '',
        telResponsable: intervention.local?.telResponsable || '',
        client: intervention.client?.raisonSocial || '',
      }));
    }
  }, [intervention]);

  // Handle form field change
  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  // Handle submit (demo only)
  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate and submit logic here
    // ...
  };

  const handleShowHistory = async () => {
    console.log('Showing history for article:', articleInfo);
    if (!articleInfo?.id) {
      toast.warn("Veuillez d'abord sélectionner un article.");
      return;
    }
    setLoadingHistory(true);
    setShowHistoryModal(true);
    const res = await interventionService.getArticleHistory(articleInfo.id);
    if (res.success) {
      setHistoryData(res.data);
    } else {
      setHistoryData([]);
      toast.error(res.message || "Erreur lors du chargement de l'historique");
    }
    setLoadingHistory(false);
  };

  const handleAddEntretien = async () => {
    if (!intervention || !articleInfo || !articleInfo.id) {
      toast.warn('Veuillez sélectionner un article et une intervention.');
      return;
    }
    const pieceDeRechangeEtServices = [
      ...selectedPieces,
      ...selectedServices
    ].map(item => (typeof item === 'object' ? { id: item.id } : { id: item })).filter(Boolean);
    try {
      const res = await interventionService.createEntretien({
        interventionId: intervention.id,
        articleId: articleInfo.id,
        pieceDeRechangeEtServices,
      });
      if (res.success) {
        toast.success('Entretien ajouté avec succès !');
      } else {
        toast.error(res.message || "Erreur lors de la création de l'entretien");
      }
    } catch (err) {
      toast.error("Erreur lors de la création de l'entretien");
    }
  };

  const handleValidateBarcode = async (scannedCode) => {
    const code = scannedCode !== undefined ? scannedCode : codeABarre;
    if (!code) return;
    
    // Validate barcode length before processing
    if (code.length > 8) {
      toast.error('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
      setBarcodeStatusColor('#ef4444');
      setArticleInfo(null);
      setFilteredFamille(null);
      setSelectsDisabled(true);
      return;
    }
    
    setBarcodeStatusColor('#22c55e');
    setArticleInfo(null);
    setFilteredFamille(null);
    setSelectsDisabled(true);
    setShowCreateArticleModal(false);
    
    try {
      const res = await articleService.getArticleByCodeABarre(code);
      if (res.success && res.data) {
        setArticleInfo(res.data);
        setBarcodeStatusColor(res.data.green ? '#22c55e' : '#ef4444');
        setSelectsDisabled(false);

        // Fetch already selected pieces for this article/intervention
        let selectedPiecesFromApi = [];
        if (res.data.id && intervention && intervention.id) {
          try {
            const selectedRes = await interventionService.getSelectedPiecesForArticle(res.data.id, intervention.id);
            if (selectedRes.success && Array.isArray(selectedRes.data)) {
              selectedPiecesFromApi = selectedRes.data;
            }
          } catch {}
        }

        if (res.data.typeArticleId) {
          const typeId = String(res.data.typeArticleId);
          const familleGroups = {};
          pieces.forEach(p => {
            const fam = (p.famille || 'Autre').trim();
            if (!familleGroups[fam]) familleGroups[fam] = [];
            if (!p.typeArticleId || p.typeArticleId === '' || p.typeArticleId === undefined) {
              familleGroups[fam].push(p);
            } else {
              const typeIds = p.typeArticleId.split(',').map(s => s.trim());
              if (typeIds.includes(typeId)) {
                familleGroups[fam].push(p);
              }
            }
          });
        console.log('this is the articleInfo', articleInfo);

          const filteredSections = Object.entries(familleGroups)
            .filter(([_, items]) => items.length > 0)
            .map(([famille, items]) => ({ famille, items }));

          if (selectedPiecesFromApi.length > 0) {
            let selectedPieceObjs = [];
            filteredSections.forEach(section => {
              if (section.famille.toLowerCase().includes('piece') || section.famille.toLowerCase().includes('entretien')) {
                selectedPiecesFromApi.forEach(sel => {
                  let found = section.items.find(p => String(p.id) === String(sel.id));
                  if (!found) {
                    found = section.items.find(p =>
                      sel.famille && sel.designation &&
                      p.famille && p.designation &&
                      sel.famille.trim().toLowerCase() === p.famille.trim().toLowerCase() &&
                      sel.designation.trim().toLowerCase() === p.designation.trim().toLowerCase()
                    );
                  }
                  if (found && !selectedPieceObjs.some(obj => String(obj.id) === String(found.id))) {
                    selectedPieceObjs.push(found);
                  }
                });
              }
            });
            setSelectedPieces(selectedPieceObjs);
          } else {
            setSelectedPieces([]);
          }
          setSelectedServices([]);
          setFilteredFamille(filteredSections.length > 0 ? filteredSections : null);
        } else {
          const familleGroups = {};
          pieces.forEach(p => {
            if (p.famille && p.famille.toLowerCase().includes('entretien') && p.designation && p.designation.toLowerCase().includes('verification')) {
              const fam = (p.famille || 'Entretien').trim();
              if (!familleGroups[fam]) familleGroups[fam] = [];
              familleGroups[fam].push(p);
            }
          });
          const filteredSections = Object.entries(familleGroups).map(([famille, items]) => ({ famille, items }));
          setSelectedPieces([]);
          setSelectedServices([]);
          setFilteredFamille(filteredSections.length > 0 ? filteredSections : null);
        }
      } else {
        setArticleInfo(null);
        setBarcodeStatusColor('#ef4444');
        setFilteredFamille(null);
        setSelectsDisabled(true);
        setShowCreateArticleModal(true);
      }
    } catch (err) {
      setArticleInfo(null);
      setBarcodeStatusColor('#ef4444');
      setFilteredFamille(null);
      setSelectsDisabled(true);
      setShowCreateArticleModal(true);
    }
  };

  // Handle QR/Barcode scan result
  const handleScanResult = (result) => {
    if (result) {
      setCodeABarre(result);
      setShowCamera(false);
      handleValidateBarcode(result);
      toast.success(`Code scanné: ${result}`);
    }
  };

  // Handle scan error
  const handleScanError = (error) => {
    console.warn('Scanner error:', error);
    // Don't show error toasts for common camera permissions/access issues
  };
  return (
    <div className={`min-h-screen w-full p-0 m-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={`w-full h-full rounded-none sm:rounded-2xl shadow-2xl border-0 sm:border ${isDarkMode ? 'bg-gray-800 sm:border-gray-700' : 'bg-white sm:border-gray-200'} px-2 sm:px-10 py-4 sm:py-10 transition-all`} style={{maxWidth: '100vw', minHeight: '100vh', margin: 0}}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4 border-b border-gray-700/20 pb-4">
          <h1 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nouvel Entretien</h1>
          <button
            onClick={() => window.history.back()}
            className={`px-5 py-2 rounded-lg text-base font-semibold shadow-sm transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <i className="fas fa-arrow-left mr-2"></i> Retour
          </button>
        </div>

        {/* Barcode input section */}
        <div className="mb-10">
          <label className="block text-base font-semibold mb-3 text-gray-700 dark:text-white">
            <i className="fas fa-barcode mr-2 text-primary-500"></i>
            Code à barres de l'article
          </label>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              type="text"
              value={codeABarre}
              onChange={e => {
                setCodeABarre(e.target.value);
              }}
              placeholder="Saisir ou scanner le code à barres"
              className={`flex-1 px-5 py-3 rounded-xl border focus:ring-2 focus:border-transparent text-lg transition-colors ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-400 focus:ring-primary-700' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
            />

            <div className="flex flex-row gap-2 items-center">
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  if (!codeABarre) return;
                  await handleValidateBarcode();
                }}
                className={`flex items-center justify-center px-5 py-3 rounded-xl text-lg font-bold shadow transition-colors ${isDarkMode ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
                title="Valider le code à barres"
                style={{marginRight: 4}}
              >
                <i className="fas fa-check"></i>
              </button>
              <button
                onClick={() => {
                  if (barcodeScanning) {
                    stopScanning();
                  } else {
                    startBarcodeScanning();
                  }
                }}
                className={`flex items-center justify-center px-5 py-3 rounded-xl text-lg font-bold shadow transition-colors ${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                title="Scanner avec la caméra"
              >
                <i className="fas fa-camera"></i>
              </button>
              <button
                type="button"
                onClick={handleShowHistory}
                className={`flex items-center justify-center px-4 py-3 rounded-xl text-lg font-bold shadow transition-colors ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                title="Historique des scans"
                style={{marginLeft: 4, backgroundColor: barcodeStatusColor}}
              >
                <i className="fas fa-history" style={{color: '#ffffff'}}></i>
              </button>
            </div>
          </div>

          {/* Camera Scanner - Updated UI */}
          {barcodeScanning && (
            <div className="mt-5 p-5 rounded-xl border-2 border-dashed border-primary-500 bg-primary-50 text-center text-primary-700 dark:bg-gray-900 dark:border-primary-700 dark:text-primary-300 shadow-lg">
              <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Scanner en cours...
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={stopScanning}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow transition-colors"
                >
                  <i className="fas fa-times mr-2"></i>
                  Arrêter
                </button>
              </div>
            </div>
          )}

          {/* Article info display */}
          {articleInfo && (
            <>
              {/* ALERT IF LOCAL OR CLIENT MISMATCH */}
              {(articleInfo.local?.adresse !== intervention?.local?.adresse ||
                articleInfo.local?.clientId !== intervention?.client?.id) && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-100 border border-red-300 text-red-800 dark:bg-red-900/50 dark:border-red-700 dark:text-red-300 flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-red-500"></i>
                  <span>
                    Attention : Cet article est rattaché à un{" "}
                    {articleInfo.local?.adresse !== intervention?.local?.adresse && (
                      <>local différent (<b>{articleInfo.local?.adresse || 'N/A'}</b>)</>
                    )}
                    {articleInfo.local?.adresse !== intervention?.local?.adresse &&
                      articleInfo.local?.clientId !== intervention?.client?.id && <> et </>}
                    {articleInfo.local?.clientId !== intervention?.client?.id && (
                      <>client différent (<b>{articleInfo.client?.raisonSocial}</b>)</>
                    )}
                    {" "}de l'intervention sélectionnée.
                  </span>
                </div>
              )}
              <div className={`mt-6 mb-2 rounded-xl p-6 shadow relative ${isDarkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-gray-50 border border-gray-200 text-gray-900'}`}
                   style={{ fontSize: '1.08rem', minHeight: 80 }}>
                {/* Top-right: edit and expand/collapse icons */}
                <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                  <button
                    className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                    title={expanded ? 'Réduire les infos article' : 'Afficher les infos article'}
                    onClick={() => setExpanded(e => !e)}
                  >
                    <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-lg text-primary-500`}></i>
                  </button>
                  <button
                    className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                    title="Modifier l'article"
                    onClick={async () => {
                      if (!articleInfo.id) return;
                      setLoadingHistory(true);
                      try {
                        const res = await articleService.getArticleById(articleInfo.id);
                        if (res.success && res.data) {
                          setEditArticleData(res.data);
                          setShowEditArticleModal(true);
                        } else {
                          toast.error(res.message || "Erreur lors du chargement de l'article");
                        }
                      } catch (err) {
                        toast.error("Erreur lors du chargement de l'article");
                      }
                      setLoadingHistory(false);
                    }}
                  >
                    <i className="fas fa-pen text-lg text-primary-500"></i>
                  </button>
                </div>
                {expanded ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Code à barres */}
                    {articleInfo.codeABarre && (
                      <div><span className="font-semibold">Code à barres:</span> {articleInfo.codeABarre}</div>
                    )}
                    {/* Numéro */}
                    {articleInfo.designation && (
                      <div><span className="font-semibold">Numéro:</span> {articleInfo.designation}</div>
                    )}
                    {/* Numéro de série */}
                    {articleInfo.numeroSerie && (
                      <div><span className="font-semibold">Numéro de série:</span> {articleInfo.numeroSerie}</div>
                    )}
                    {/* Famille */}
                    {articleInfo.typeArticle && articleInfo.typeArticle.famille && articleInfo.typeArticle.famille.designation && (
                      <div><span className="font-semibold">Famille:</span> {articleInfo.typeArticle.famille.designation}</div>
                    )}
                    {/* Type d'article */}
                    {articleInfo.typeArticle && articleInfo.typeArticle.designation && (
                      <div><span className="font-semibold">Type d'article:</span> {articleInfo.typeArticle.designation}</div>
                    )}
                    {/* Capacité */}
                    {articleInfo.capacite !== undefined && articleInfo.capacite !== null && (
                      <div><span className="font-semibold">Capacité:</span> {articleInfo.capacite}</div>
                    )}
                    {/* Pression */}
                    {typeof articleInfo.pressionPermanente === 'boolean' && (
                      <div>
                        <span className="font-semibold">Pression:</span> {articleInfo.pressionPermanente ? 'Permanente' : 'Auxiliaire'}
                      </div>
                    )}
                    {/* Local */}
                    {articleInfo.local && articleInfo.local.adresse && (
                      <div><span className="font-semibold">Local:</span> {articleInfo.local.adresse}</div>
                    )}
                    {/* Emplacement */}
                    {articleInfo.emplacement && (
                      <div><span className="font-semibold">Emplacement:</span> {articleInfo.emplacement}</div>
                    )}
                    {/* Date de fabrication */}
                    {articleInfo.datetimeFabrication && (
                      <div>
                        <span className="font-semibold">Date de fabrication:</span>{' '}
                        {new Date(articleInfo.datetimeFabrication).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                    {/* Observation client */}
                    {articleInfo.observationClient && (
                      <div><span className="font-semibold">Observation client:</span> {articleInfo.observationClient}</div>
                    )}
                    {/* Observation */}
                    {articleInfo.observation && (
                      <div><span className="font-semibold">Observation:</span> {articleInfo.observation}</div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-16 text-gray-400 dark:text-gray-500 text-base font-medium select-none">
                    Cliquez pour afficher les détails de l'article
                  </div>
                )}
              </div>
              {/* Edit Article Modal */}
              {showEditArticleModal && editArticleData && (
                <ArticleNotFoundModal
                  isOpen={showEditArticleModal}
                  onClose={() => setShowEditArticleModal(false)}
                  codeABarre={editArticleData.codeABarre}
                  onSuccess={updatedArticle => {
                    setShowEditArticleModal(false);
                    if (updatedArticle) {
                      setArticleInfo(updatedArticle);
                      toast.success('Article modifié avec succès !');
                    }
                  }}
                  initialData={editArticleData}
                  mode="edit"
                />
              )}
            </>
          )}
        </div>

        {/* Grouped by famille from backend with search filter and barcode logic */}
        <div className="flex flex-col gap-8 mb-10">
          {/* Divider and improved search filter placement */}
          <div className="w-full border-t border-gray-200 dark:border-gray-700 my-6"></div>
          <div className="w-full flex justify-center">
            <div className="w-full sm:w-2/3 md:w-1/2 lg:w-2/5 bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-sm px-4 py-3 mb-2 flex items-center gap-3 border border-gray-200 dark:border-gray-700">
              <i className="fas fa-search text-gray-400 text-lg mr-2" />
              <input
                type="text"
                placeholder="Rechercher une pièce, un service ou une famille..."
                className="flex-1 bg-transparent outline-none border-0 text-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                value={searchTerm || ''}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Show custom article not found modal if needed */}
          {showCreateArticleModal && (
            <ArticleNotFoundModal
              isOpen={showCreateArticleModal}
              onClose={() => setShowCreateArticleModal(false)}
              codeABarre={codeABarre}
              onSuccess={(createdArticle) => {
                setShowCreateArticleModal(false);
                if (createdArticle) {
                  setArticleInfo(createdArticle);
                  setBarcodeStatusColor(createdArticle.green ? '#22c55e' : '#ef4444');
                  setSelectsDisabled(false);
                  // Re-run the famille/pieces filtering logic as in the barcode check button
                  if (createdArticle.typeArticleId) {
                    const typeId = String(createdArticle.typeArticleId);
                    const familleGroups = {};
                    pieces.forEach(p => {
                      const fam = (p.famille || 'Autre').trim();
                      if (!familleGroups[fam]) familleGroups[fam] = [];
                      if (!p.typeArticleId || p.typeArticleId === '' || p.typeArticleId === undefined) {
                        familleGroups[fam].push(p);
                      } else {
                        const typeIds = p.typeArticleId.split(',').map(s => s.trim());
                        if (typeIds.includes(typeId)) {
                          familleGroups[fam].push(p);
                        }
                      }
                    });
                    const filteredSections = Object.entries(familleGroups)
                      .filter(([_, items]) => items.length > 0)
                      .map(([famille, items]) => ({ famille, items }));
                    setSelectedPieces([]);
                    setSelectedServices([]);
                    setFilteredFamille(filteredSections.length > 0 ? filteredSections : null);
                  } else {
                    // Fallback: show entretien/verification pieces grouped by famille
                    const familleGroups = {};
                    pieces.forEach(p => {
                      if (p.famille && p.famille.toLowerCase().includes('entretien') && p.designation && p.designation.toLowerCase().includes('verification')) {
                        const fam = (p.famille || 'Entretien').trim();
                        if (!familleGroups[fam]) familleGroups[fam] = [];
                        familleGroups[fam].push(p);
                      }
                    });
                    const filteredSections = Object.entries(familleGroups).map(([famille, items]) => ({ famille, items }));
                    setSelectedPieces([]);
                    setSelectedServices([]);
                    setFilteredFamille(filteredSections.length > 0 ? filteredSections : null);
                  }
                } else {
                  setArticleInfo(null);
                  setBarcodeStatusColor('#ef4444');
                  setFilteredFamille(null);
                  setSelectsDisabled(true);
                }
              }}
            />
          )}

          {/* Show selects only if not in create article mode */}
          {!showCreateArticleModal && (
            filteredFamille ? (
              <>
                {Array.isArray(filteredFamille) ? (
                  filteredFamille
                    .map((section, idx) => {
                      // Filter famille and items by search term
                      const term = (searchTerm || '').toLowerCase();
                      const familleMatch = section.famille.toLowerCase().includes(term);
                      const filteredItems = term
                        ? section.items.filter(i =>
                            (i.designation && i.designation.toLowerCase().includes(term))
                          )
                        : section.items;
                      if (!familleMatch && filteredItems.length === 0) return null;
                      const isPieceOrEntretien = section.famille.toLowerCase().includes('piece') || section.famille.toLowerCase().includes('entretien');
                      return (
                        <MultiCheckboxSelect
                          key={section.famille + idx}
                          label={section.famille}
                          options={filteredItems}
                          value={
                            isPieceOrEntretien
                              ? selectedPieces.map(p => String(p.id))
                              : section.famille.toLowerCase().includes('service') || section.famille.toLowerCase().includes('maintenace')
                              ? selectedServices.map(s => String(s.id))
                              : []
                          }
                          onChange={
                            isPieceOrEntretien
                              ? (ids => {
                                  // Merge new selection from this group with selections from other groups
                                  setSelectedPieces(prev => {
                                    // Remove all previous selections from this group
                                    const prevNotInThisGroup = prev.filter(p => !section.items.some(i => String(i.id) === String(p.id)));
                                    // Add new selections from this group
                                    const newSelected = section.items.filter(p => ids.map(String).includes(String(p.id)));
                                    // Merge and deduplicate by id
                                    const merged = [...prevNotInThisGroup, ...newSelected];
                                    const unique = [];
                                    const seen = new Set();
                                    for (const p of merged) {
                                      const idStr = String(p.id);
                                      if (!seen.has(idStr)) {
                                        unique.push(p);
                                        seen.add(idStr);
                                      }
                                    }
                                    return unique;
                                  });
                                })
                              : section.famille.toLowerCase().includes('service') || section.famille.toLowerCase().includes('maintenace')
                              ? (ids => {
                                  setSelectedServices(prev => {
                                    const prevNotInThisGroup = prev.filter(s => !section.items.some(i => String(i.id) === String(s.id)));
                                    const newSelected = section.items.filter(s => ids.map(String).includes(String(s.id)));
                                    const merged = [...prevNotInThisGroup, ...newSelected];
                                    const unique = [];
                                    const seen = new Set();
                                    for (const s of merged) {
                                      const idStr = String(s.id);
                                      if (!seen.has(idStr)) {
                                        unique.push(s);
                                        seen.add(idStr);
                                      }
                                    }
                                    return unique;
                                  });
                                })
                              : () => {}
                          }
                          disabled={selectsDisabled}
                        />
                      );
                    })
                    .filter(Boolean)
                ) : null}
              </>
            ) : (
              (() => {
                const grouped = Object.entries(
                  pieces.reduce((acc, item) => {
                    const fam = (item.famille || 'Autre').trim();
                    if (!acc[fam]) acc[fam] = [];
                    acc[fam].push(item);
                    return acc;
                  }, {})
                )
                  .filter(([famille, items]) => {
                    if (!searchTerm) return true;
                    const term = searchTerm.toLowerCase();
                    return (
                      famille.toLowerCase().includes(term) ||
                      items.some(i =>
                        (i.designation && i.designation.toLowerCase().includes(term))
                      )
                    );
                  })
                  .map(([famille, items]) => {
                    // Filter items in famille by search term
                    const filteredItems = searchTerm
                      ? items.filter(i =>
                          (i.designation && i.designation.toLowerCase().includes(searchTerm.toLowerCase()))
                        )
                      : items;
                    if (!filteredItems.length) return null;
                    return (
                      <div key={famille}>
                        <MultiCheckboxSelect
                          label={famille}
                          options={filteredItems}
                          value={
                            famille.toLowerCase().includes('piece')
                              ? selectedPieces
                              : famille.toLowerCase().includes('service') || famille.toLowerCase().includes('entretien') || famille.toLowerCase().includes('maintenace')
                              ? selectedServices
                              : []
                          }
                          onChange={
                            famille.toLowerCase().includes('piece')
                              ? (items => {
                                  setSelectedPieces(prev => {
                                    // Remove all previous selections from this group
                                    const prevNotInThisGroup = prev.filter(p => !filteredItems.some(i => String(i.id) === String(p.id)));
                                    // Add new selections from this group
                                    const newSelected = filteredItems.filter(p => items.map(String).includes(String(p.id)));
                                    const merged = [...prevNotInThisGroup, ...newSelected];
                                    const unique = [];
                                    const seen = new Set();
                                    for (const p of merged) {
                                      const idStr = String(p.id);
                                      if (!seen.has(idStr)) {
                                        unique.push(p);
                                        seen.add(idStr);
                                      }
                                    }
                                    return unique;
                                  });
                                })
                              : famille.toLowerCase().includes('service') || famille.toLowerCase().includes('entretien') || famille.toLowerCase().includes('maintenace')
                              ? (items => {
                                  setSelectedServices(prev => {
                                    // Remove all previous selections from this group
                                    const prevNotInThisGroup = prev.filter(s => !filteredItems.some(i => String(i.id) === String(s.id)));
                                    // Add new selections from this group
                                    const newSelected = filteredItems.filter(s => items.map(String).includes(String(s.id)));
                                    const merged = [...prevNotInThisGroup, ...newSelected];
                                    const unique = [];
                                    const seen = new Set();
                                    for (const s of merged) {
                                      const idStr = String(s.id);
                                      if (!seen.has(idStr)) {
                                        unique.push(s);
                                        seen.add(idStr);
                                      }
                                    }
                                    return unique;
                                  });
                                })
                              : () => {}
                          }
                          disabled={selectsDisabled}
                        />
                      </div>
                    );
                  })
                  .filter(Boolean);
                if (grouped.length === 0) {
                  return (
                    <div className="text-center text-lg text-gray-400 dark:text-gray-500 py-8">
                      <i className="fas fa-search-minus text-2xl mb-2 block" />
                      Aucun résultat trouvé pour cette recherche.
                    </div>
                  );
                }
                return grouped;
              })()
            )
          )}
        </div>

        {loadingPieces && <div className="text-center text-gray-500 py-4">Chargement des pièces/services...</div>}
        {errorPieces && <div className="text-center text-red-500 py-4">{errorPieces}</div>}
      </div>

      <button
        onClick={handleAddEntretien}
        className="fixed bottom-8 right-8 z-50 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-8 py-4 text-xl font-bold flex items-center gap-3 transition-all"
        style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)' }}
      >
        <i className="fas fa-plus-circle text-2xl"></i>
        Enregistrer
      </button>

      <ArticleHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        history={historyData}
        isDarkMode={isDarkMode}
        loading={loadingHistory}
      />
    </div>
  );
};

export default EntretienPage;